import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from './ui/sheet';
import {
  ShoppingCart,
  Package,
  FileText,
  Wallet,
  LogOut,
  Users,
  Box,
  Phone,
  Menu,
  BarChart3
} from 'lucide-react';
import { Badge } from './ui/badge';

const Navbar = ({ cartCount = 0 }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === 'admin';

  const isActive = (path) => location.pathname === path;

  const navLinks = isAdmin
    ? [
        { path: '/', label: 'Clienți', icon: Users, testId: 'nav-clients' },
        { path: '/admin/products', label: 'Produse', icon: Box, testId: 'nav-admin-products' },
        { path: '/admin/orders', label: 'Comenzi', icon: Package, testId: 'nav-admin-orders' },
        { path: '/admin/invoices', label: 'Facturi', icon: FileText, testId: 'nav-admin-invoices' },
        { path: '/admin/analytics', label: 'Rapoarte', icon: BarChart3, testId: 'nav-admin-analytics' },
        { path: '/contact', label: 'Contact', icon: Phone, testId: 'nav-contact' }
      ]
    : [
        { path: '/', label: 'Produse', icon: Box, testId: 'nav-products' },
        { path: '/orders', label: 'Comenzi', icon: Package, testId: 'nav-orders' },
        { path: '/invoices', label: 'Facturi', icon: FileText, testId: 'nav-invoices' },
        { path: '/balance', label: 'Sold', icon: Wallet, testId: 'nav-balance' },
        { path: '/contact', label: 'Contact', icon: Phone, testId: 'nav-contact' }
      ];

  const NavLinksList = ({ onLinkClick, vertical = false }) => (
    <div className={vertical ? 'flex flex-col space-y-2 w-full' : 'flex space-x-1'}>
      {navLinks.map((link) => {
        const Icon = link.icon;
        return (
          <Link
            key={link.path}
            to={link.path}
            data-testid={link.testId}
            onClick={onLinkClick}
            className={`flex items-center space-x-2 px-4 py-3 rounded-md transition-all ${
              isActive(link.path)
                ? vertical
                  ? 'bg-blue-900 text-white font-semibold'
                  : 'bg-white text-blue-900 font-semibold'
                : vertical
                ? 'text-slate-700 hover:bg-blue-50'
                : 'text-blue-100 hover:bg-blue-800'
            }`}
            style={{ fontFamily: 'IBM Plex Sans' }}
          >
            <Icon className="w-5 h-5" />
            <span>{link.label}</span>
          </Link>
        );
      })}
    </div>
  );

  return (
    <nav className="bg-blue-900 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Mobile menu button */}
          <div className="md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-blue-800"
                  data-testid="mobile-menu-button"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] bg-white p-0 flex flex-col h-full">
                <SheetHeader className="p-6 border-b border-slate-200 bg-blue-900 shrink-0">
                  <SheetTitle className="flex items-center justify-center">
                    <div className="bg-white p-2 rounded-lg">
                      <img src="/logo.png" alt="TUNA FOOD BBD" className="h-12 w-auto" />
                    </div>
                  </SheetTitle>
                </SheetHeader>
                <div className="flex-1 min-h-0 overflow-y-auto p-4">
                  <NavLinksList onLinkClick={() => setMobileOpen(false)} vertical />
                </div>

                <div className="border-t border-slate-200 p-4 shrink-0 bg-white">
                  <div className="px-2 py-2 mb-2">
                    <div className="font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans' }}>
                      {user?.name}
                    </div>
                    <div className="text-slate-500 text-xs truncate" style={{ fontFamily: 'IBM Plex Sans' }}>
                      {user?.email}
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      setMobileOpen(false);
                      logout();
                    }}
                    variant="outline"
                    className="w-full border-red-500 text-red-600 hover:bg-red-50"
                    data-testid="mobile-logout-button"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Ieșire
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="bg-white p-2 rounded-lg shadow-md transition-transform group-hover:scale-105">
              <img
                src="/logo.png"
                alt="TUNA FOOD BBD"
                className="h-12 md:h-16 w-auto"
                data-testid="navbar-logo"
              />
            </div>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex flex-1 justify-center">
            <NavLinksList />
          </div>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            {!isAdmin && cartCount > 0 && (
              <div className="relative">
                <ShoppingCart className="w-6 h-6" />
                <Badge
                  className="absolute -top-2 -right-2 bg-orange-600 text-white"
                  data-testid="cart-badge"
                >
                  {cartCount}
                </Badge>
              </div>
            )}

            <div className="hidden md:block text-sm" style={{ fontFamily: 'IBM Plex Sans' }}>
              <div className="font-semibold">{user?.name}</div>
              <div className="text-blue-200 text-xs">{user?.email}</div>
            </div>

            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              className="hidden md:flex bg-transparent border-white text-white hover:bg-white hover:text-blue-900"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Ieșire
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
